import React from 'react';
import { motion } from 'framer-motion';
import { ChevronDown, Star } from 'lucide-react';

/* Decorative SVG elements */
const FloatingScissors = ({ className }) => (
  <svg className={className} viewBox="0 0 60 60" fill="none" xmlns="http://www.w3.org/2000/svg">
    <circle cx="14" cy="14" r="10" stroke="#F5E6D3" strokeWidth="2" fill="none" opacity="0.6" />
    <circle cx="46" cy="14" r="10" stroke="#F5E6D3" strokeWidth="2" fill="none" opacity="0.6" />
    <line x1="21" y1="21" x2="39" y2="39" stroke="#C9956C" strokeWidth="2.5" strokeLinecap="round" />
    <line x1="39" y1="21" x2="21" y2="39" stroke="#C9956C" strokeWidth="2.5" strokeLinecap="round" />
  </svg>
);

const FloatingFlower = ({ className }) => (
  <svg className={className} viewBox="0 0 80 80" fill="none" xmlns="http://www.w3.org/2000/svg">
    <circle cx="40" cy="20" r="10" fill="#C9956C" opacity="0.5" />
    <circle cx="60" cy="40" r="10" fill="#C9956C" opacity="0.4" />
    <circle cx="40" cy="60" r="10" fill="#C9956C" opacity="0.5" />
    <circle cx="20" cy="40" r="10" fill="#C9956C" opacity="0.4" />
    <circle cx="55" cy="25" r="9" fill="#F5E6D3" opacity="0.3" />
    <circle cx="55" cy="55" r="9" fill="#F5E6D3" opacity="0.3" />
    <circle cx="25" cy="55" r="9" fill="#F5E6D3" opacity="0.3" />
    <circle cx="25" cy="25" r="9" fill="#F5E6D3" opacity="0.3" />
    <circle cx="40" cy="40" r="12" fill="#C9956C" opacity="0.7" />
    <circle cx="40" cy="40" r="7" fill="#F5E6D3" opacity="0.9" />
  </svg>
);

const FloatingSparkle = ({ className }) => (
  <svg className={className} viewBox="0 0 40 40" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M20 2L22.5 17.5L38 20L22.5 22.5L20 38L17.5 22.5L2 20L17.5 17.5L20 2Z" fill="#C9956C" opacity="0.8" />
    <path d="M20 8L21.5 18.5L32 20L21.5 21.5L20 32L18.5 21.5L8 20L18.5 18.5L20 8Z" fill="#F5E6D3" opacity="0.5" />
  </svg>
);

const FloatingComb = ({ className }) => (
  <svg className={className} viewBox="0 0 50 80" fill="none" xmlns="http://www.w3.org/2000/svg">
    <rect x="5" y="5" width="40" height="18" rx="4" fill="none" stroke="#F5E6D3" strokeWidth="2" opacity="0.5" />
    <rect x="10" y="23" width="4" height="52" rx="2" fill="#C9956C" opacity="0.6" />
    <rect x="18" y="23" width="4" height="48" rx="2" fill="#C9956C" opacity="0.5" />
    <rect x="26" y="23" width="4" height="52" rx="2" fill="#C9956C" opacity="0.6" />
    <rect x="34" y="23" width="4" height="48" rx="2" fill="#C9956C" opacity="0.5" />
  </svg>
);

const FloatingLeaf = ({ className }) => (
  <svg className={className} viewBox="0 0 60 80" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M30 5C30 5 55 25 50 55C45 70 30 75 30 75C30 75 15 70 10 55C5 25 30 5 30 5Z"
      fill="none" stroke="#C9956C" strokeWidth="2" opacity="0.5" />
    <path d="M30 5C30 5 45 30 42 55C40 65 30 75 30 75" stroke="#F5E6D3" strokeWidth="1.5" strokeDasharray="3 3" opacity="0.4" />
  </svg>
);

export default function Hero() {
  const scrollTo = (href) => {
    const el = document.querySelector(href);
    if (el) el.scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <section
      id="home"
      className="relative min-h-screen flex items-center justify-center overflow-hidden"
      style={{
        background: 'linear-gradient(135deg, #1A1A1A 0%, #3d1528 30%, #6B2737 60%, #8B4A2A 80%, #C9956C 100%)',
      }}
    >
      {/* Background texture overlay */}
      <div
        className="absolute inset-0 opacity-10"
        style={{
          backgroundImage: `radial-gradient(circle at 25% 25%, #C9956C 0%, transparent 50%),
                            radial-gradient(circle at 75% 75%, #F5E6D3 0%, transparent 50%)`,
        }}
      />

      {/* Floating decorative elements */}
      <div className="absolute inset-0 pointer-events-none overflow-hidden">
        {/* Top left scissors */}
        <div className="absolute top-[8%] left-[5%] float-a opacity-60 hidden sm:block">
          <FloatingScissors className="w-16 h-16" />
        </div>

        {/* Top right flower */}
        <div className="absolute top-[6%] right-[8%] float-b opacity-70">
          <FloatingFlower className="w-20 h-20 md:w-28 md:h-28" />
        </div>

        {/* Middle left sparkle */}
        <div className="absolute top-[35%] left-[3%] float-c opacity-80">
          <FloatingSparkle className="w-10 h-10 md:w-14 md:h-14" />
        </div>

        {/* Bottom left leaf */}
        <div className="absolute bottom-[20%] left-[6%] float-a opacity-50 hidden md:block">
          <FloatingLeaf className="w-14 h-20" />
        </div>

        {/* Bottom right comb */}
        <div className="absolute bottom-[25%] right-[5%] float-b opacity-60 hidden sm:block">
          <FloatingComb className="w-10 h-16" />
        </div>

        {/* Extra sparkles */}
        <div className="absolute top-[55%] right-[12%] float-c opacity-70">
          <FloatingSparkle className="w-8 h-8 md:w-10 md:h-10" />
        </div>
        <div className="absolute top-[20%] left-[18%] float-b opacity-50 hidden lg:block">
          <FloatingSparkle className="w-6 h-6" />
        </div>
        <div className="absolute bottom-[40%] right-[18%] float-a opacity-40 hidden lg:block">
          <FloatingFlower className="w-16 h-16" />
        </div>

        {/* Large soft glow orbs */}
        <div
          className="absolute -top-20 -right-20 w-96 h-96 rounded-full opacity-20"
          style={{ background: 'radial-gradient(circle, #C9956C, transparent 70%)' }}
        />
        <div
          className="absolute -bottom-20 -left-20 w-80 h-80 rounded-full opacity-15"
          style={{ background: 'radial-gradient(circle, #F5E6D3, transparent 70%)' }}
        />
      </div>

      {/* Main content */}
      <div className="relative z-10 text-center px-4 sm:px-6 max-w-5xl mx-auto">
        {/* Badge */}
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.2 }}
          className="inline-flex items-center gap-2 mb-6 px-4 py-2 rounded-full border border-roseGold/40 bg-white/5 backdrop-blur-sm"
        >
          <div className="flex gap-0.5">
            {[...Array(5)].map((_, i) => (
              <Star key={i} className="w-3.5 h-3.5 fill-roseGold text-roseGold" />
            ))}
          </div>
          <span className="font-inter text-champagne/90 text-xs font-medium tracking-widest uppercase">
            4.9 · 132 Reviews on Google
          </span>
        </motion.div>

        {/* Main headline */}
        <motion.h1
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.9, delay: 0.4, ease: [0.16, 1, 0.3, 1] }}
          className="font-playfair font-bold leading-tight mb-6"
          style={{ fontSize: 'clamp(2.6rem, 6vw, 5rem)' }}
        >
          <span className="shimmer-text block">Where Beauty</span>
          <span className="shimmer-text block">Meets Luxury</span>
        </motion.h1>

        {/* Decorative line */}
        <motion.div
          initial={{ scaleX: 0 }}
          animate={{ scaleX: 1 }}
          transition={{ duration: 0.8, delay: 0.8 }}
          className="flex items-center justify-center gap-3 mb-6"
        >
          <div className="h-px w-16 bg-gradient-to-r from-transparent to-roseGold" />
          <FloatingSparkle className="w-5 h-5 opacity-70" />
          <div className="h-px w-16 bg-gradient-to-l from-transparent to-roseGold" />
        </motion.div>

        {/* Subheading */}
        <motion.p
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, delay: 0.7 }}
          className="font-inter text-champagne/80 text-base sm:text-lg md:text-xl font-light max-w-2xl mx-auto mb-10 leading-relaxed"
        >
          Abuja's premium hair salon &amp; spa experience —{' '}
          <span className="text-roseGold font-medium">Wuse 2</span>
        </motion.p>

        {/* CTA buttons */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, delay: 0.9 }}
          className="flex flex-col sm:flex-row gap-4 justify-center items-center"
        >
          <button
            onClick={() => scrollTo('#booking')}
            className="group px-8 py-4 rounded-full bg-gradient-to-r from-roseGold to-roseGoldDark text-white font-inter font-semibold text-sm tracking-wide shadow-xl shadow-roseGold/40 hover:shadow-roseGold/60 hover:scale-105 transition-all duration-300 min-w-[220px]"
          >
            <span className="flex items-center justify-center gap-2">
              Book an Appointment
              <span className="group-hover:translate-x-1 transition-transform duration-200">→</span>
            </span>
          </button>

          <button
            onClick={() => scrollTo('#services')}
            className="px-8 py-4 rounded-full border-2 border-champagne/50 text-champagne font-inter font-semibold text-sm tracking-wide hover:bg-white/10 hover:border-champagne transition-all duration-300 min-w-[220px]"
          >
            Explore Services
          </button>
        </motion.div>

        {/* Location pill */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.6, delay: 1.2 }}
          className="mt-10 inline-flex items-center gap-2 text-champagne/50 text-xs font-inter font-medium tracking-wider"
        >
          <span className="w-1.5 h-1.5 rounded-full bg-roseGold animate-pulse inline-block" />
          CVS Plaza, Adetokunbo Ademola Crescent, Wuse 2, Abuja
        </motion.div>
      </div>

      {/* Scroll indicator */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.5, duration: 0.6 }}
        className="absolute bottom-8 left-1/2 -translate-x-1/2 flex flex-col items-center gap-1 cursor-pointer"
        onClick={() => scrollTo('#marquee')}
      >
        <span className="font-inter text-champagne/40 text-xs tracking-widest uppercase">Scroll</span>
        <div className="scroll-bounce">
          <ChevronDown className="w-5 h-5 text-roseGold" />
        </div>
      </motion.div>
    </section>
  );
}
